import { useLazyQuery } from '@apollo/client/react';
import { useTheme } from '@emotion/react';
import styled from '@emotion/styled';
import { css } from '@styled-system/css';
import { useAtom, useAtomValue } from 'jotai';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Box, Checkbox as DefaultCheckbox, Flex, List, ScrollBox } from '~/components/blocks';
import { BASE_DATE } from '~/constants/organization_notification';
import {
  GetEntriesNotificationListDocument,
  GetEntriesNotificationListQuery,
  OrganizationEntryNotificationFragment,
} from '~/graphql/generated/graphql';
import { getMe } from '~/graphql/utility';
import { usePreviousValue } from '~/hooks/use-previous-value';
import { notificationState } from '~/state/layouts/SharedAppShell/atoms';
import { entryNotificationDrawerState } from '~/state/partials/entry_notification_drawer/atoms';

import { EntryNotification } from './EntryNotification';

const formatOrganizationNotifications = (data: GetEntriesNotificationListQuery) => {
  const me = getMe(data);

  return me
    ? me.organization.organizationNotifications.nodes.map(
        (node) => node as OrganizationEntryNotificationFragment,
      )
    : [];
};

const getTotalPages = (data: GetEntriesNotificationListQuery) => {
  return getMe(data)?.organization.organizationNotifications.pagesCount || 1;
};

const ListRoot = styled(List)(() =>
  css({
    borderTop: 'none',
  }),
);

const Checkbox = styled(DefaultCheckbox)(({ theme }) =>
  css({
    fontSize: theme.fontSizes.s,
  }),
);

export const EntryNotificationList = () => {
  const theme = useTheme();
  const scrollRef = useRef<RefAttributeType<typeof ScrollBox> | null>(null);

  const { entryCount } = useAtomValue(notificationState);
  const [state, setState] = useAtom(entryNotificationDrawerState);
  const [getEntryNotificationList, { loading, data }] = useLazyQuery(
    GetEntriesNotificationListDocument,
    {
      fetchPolicy: 'network-only',
    },
  );

  useEffect(() => {
    if (loading) return;
    if (!data) return;

    setState((_state) => ({
      ..._state,
      totalPage: getTotalPages(data),
      totalCount: getMe(data)?.organization.organizationNotifications.nodesCount ?? 0,
    }));
    scrollRef.current?.toTop();
  }, [data, loading, setState]);
  const prevData = usePreviousValue(data);
  const currentData = data || prevData;
  const notifications = useMemo(
    () => (currentData ? formatOrganizationNotifications(currentData) : []),
    [currentData],
  );
  const [isOnlyUnread, setIsOnlyUnread] = useState<boolean>(true);

  const handleOnlyUnreadCheck = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setState((_state) => ({ ..._state, page: 1 }));
      setIsOnlyUnread(e.currentTarget.checked);
    },
    [setState],
  );

  useEffect(() => {
    getEntryNotificationList({
      variables: {
        page: state.page,
        perPage: state.perPage,
        from: BASE_DATE,
        onlyUncompleted: isOnlyUnread,
      },
    });
  }, [getEntryNotificationList, state.page, state.perPage, entryCount, isOnlyUnread]);

  return (
    <Box overflow="auto" height="100%">
      <ScrollBox ref={scrollRef} loading={loading}>
        <Flex paddingY={theme.space.m} justifyContent="flex-end">
          <Checkbox
            label="未確認のみ表示"
            checked={isOnlyUnread}
            onChange={handleOnlyUnreadCheck}
          />
        </Flex>
        {!loading && notifications.length === 0 ? (
          <Flex justifyContent="center">
            <Box padding={theme.space.l}>Web問診・チェックイン通知はありません</Box>
          </Flex>
        ) : (
          <ListRoot selectable>
            {notifications.map((notification) => {
              return <EntryNotification key={notification.id} entryNotification={notification} />;
            })}
          </ListRoot>
        )}
      </ScrollBox>
    </Box>
  );
};
